import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

interface WorkdayClientThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const WorkdayClientThemeContext = createContext<WorkdayClientThemeContextType>({
  theme: 'light',
  toggleTheme: () => {},
});

function readSavedWorkdayTheme(): Theme {
  try {
    const saved = localStorage.getItem('workday-client-theme') as Theme | null;
    return saved === 'light' || saved === 'dark' ? saved : 'light';
  } catch {
    return 'light';
  }
}

export function WorkdayClientThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(readSavedWorkdayTheme);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('workday-client-theme') as Theme | null;
      if (saved === 'light' || saved === 'dark') {
        setTheme(saved);
        document.documentElement.setAttribute('data-workday-client-theme', saved);
      } else {
        // No saved preference — ensure attribute is clean
        document.documentElement.removeAttribute('data-workday-client-theme');
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('workday-client-theme', theme);
    } catch { /* ignore */ }
    document.documentElement.setAttribute('data-workday-client-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <WorkdayClientThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </WorkdayClientThemeContext.Provider>
  );
}

export function useWorkdayClientTheme() {
  return useContext(WorkdayClientThemeContext);
}
